import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import BuildIcon from "@mui/icons-material/Build";
import HomeIcon from "@mui/icons-material/Home";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  formatOrgName,
  isSyntheticEmail,
  RolesRow,
} from "pages/components/SidebarOrgUserExtras";
import toast from "react-hot-toast";
import { useVendor } from "context/VendorContext";
import Logo from "assets/images/logos/flex2logo.png";
import { cc } from "afms/theme/commandCenterTheme";
import {
  BACKOFFICE_ORG_ID,
  SYSTEM_DISPLAY_NAMES,
  getAppMode,
  getDefaultRoute,
  selectUserDetails,
  systemNameForAppMode,
} from "config/appMode";
import { setMySystemDefaultRoute } from "api/users.api";
import { getUser, logoutUser } from "redux-store/auth/actions";
import { getFixedOpsPortalRoutes } from "./FixedOpsPortalNavigation";

// Industrial blue — picked to differentiate from AFMS green (#2ed66f),
// LCP red (#d32f2f), Energy orange (#ff9100). Navy backdrop reused from
// the cc theme keeps Fixed Ops in the same "serious operations" family
// as AFMS / LCP.
const FIXED_OPS_ACCENT = "#1976d2";
const FIXED_OPS_ACCENT_HOVER = "#42a5f5";
const FIXED_OPS_ACTIVE_SHADOW = "0 10px 20px rgba(25,118,210,0.32)";
const FIXED_OPS_HOVER_BG = "rgba(25,118,210,0.18)";

const itemSx = (active, depth) => ({
  mx: 1.25,
  mb: 0.5,
  pl: 1.4 + depth * 1.8,
  pr: 1.25,
  py: depth === 0 ? 1.05 : 0.85,
  borderRadius: "8px",
  color: active ? "#fff" : "rgba(255,255,255,0.72)",
  bgcolor: active ? FIXED_OPS_ACCENT : "transparent",
  boxShadow: active ? FIXED_OPS_ACTIVE_SHADOW : "none",
  "&:hover": {
    bgcolor: active ? FIXED_OPS_ACCENT : FIXED_OPS_HOVER_BG,
    color: "#fff",
  },
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: depth === 0 ? 34 : 24,
  },
});

const routePattern = (route) => {
  if (!route) return null;
  const escaped = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/:[^/]+/g, "[^/]+")}(?:/)?$`);
};

const routeMatches = (route, pathname) => {
  if (!route) return false;
  if (route.includes(":")) return routePattern(route).test(pathname);
  return pathname === route || pathname.startsWith(`${route}/`);
};

const routeHasActiveChild = (route, pathname) => {
  if (routeMatches(route.route, pathname)) return true;
  return (route.collapse || []).some((child) => routeHasActiveChild(child, pathname));
};

const collectActiveKeys = (routeList, pathname, keys = {}) => {
  routeList.forEach((route) => {
    const routeKey = route.key || route.name;
    if (route.collapse && routeHasActiveChild(route, pathname)) {
      keys[routeKey] = true;
      collectActiveKeys(route.collapse, pathname, keys);
    }
  });
  return keys;
};

const OPEN_KEYS_STORAGE = "fixedOpsSidebarOpenKeys";

const readStoredOpenKeys = () => {
  try {
    const raw = window.localStorage.getItem(OPEN_KEYS_STORAGE);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch (e) {
    return {};
  }
};

const writeStoredOpenKeys = (keys) => {
  try {
    window.localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(keys));
  } catch (e) {
    /* localStorage unavailable — expansion just won't persist across reloads */
  }
};

function FixedOpsPortalSidebar({ onNavigate = () => {}, onSwitchSystem }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { vendorProfile, portalCapabilities, isVendorOrg } = useVendor();
  const user = useSelector((state) => state.auth.user);
  const normalizedDetails = selectUserDetails(user);

  const orgContext = useMemo(
    () => ({
      isVendorOrg: isVendorOrg === true,
      isEnterpriseOrganization:
        normalizedDetails?.isEnterpriseOrganization === true,
      // VendorTypes.name list off the org (OrganizationVendorTypes). Drives
      // the requiresVendorType gate — e.g. "Software & Subscriptions" for the
      // Software Catalog group.
      vendorTypes: normalizedDetails?.organizationVendorTypes || [],
      role: normalizedDetails?.role || "",
    }),
    [
      isVendorOrg,
      normalizedDetails?.isEnterpriseOrganization,
      normalizedDetails?.organizationVendorTypes,
      normalizedDetails?.role,
    ]
  );

  const homeRoute = useMemo(
    () => getDefaultRoute(normalizedDetails) || "/",
    [normalizedDetails]
  );

  const visibleRoutes = useMemo(
    () => getFixedOpsPortalRoutes(portalCapabilities, orgContext),
    [portalCapabilities, orgContext]
  );

  const [openKeys, setOpenKeys] = useState(readStoredOpenKeys);

  useEffect(() => {
    setOpenKeys((current) => ({ ...current, ...collectActiveKeys(visibleRoutes, pathname) }));
  }, [visibleRoutes, pathname]);

  useEffect(() => {
    writeStoredOpenKeys(openKeys);
  }, [openKeys]);

  const handleRoute = (route) => {
    if (route.href) {
      window.open(route.href, "_blank", "noreferrer");
      return;
    }
    if (route.route) {
      navigate(route.route);
      onNavigate();
    }
  };

  const renderRoute = (route, depth = 0) => {
    if (route.type === "title") {
      return (
        <Typography
          key={route.key}
          variant="caption"
          sx={{
            display: "block",
            px: 2.5,
            pt: 2,
            pb: 0.75,
            color: "rgba(255,255,255,0.42)",
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          {route.title}
        </Typography>
      );
    }

    if (route.type === "divider") {
      return <Divider key={route.key} sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />;
    }

    const key = route.key || route.name || route.route;
    const active = routeHasActiveChild(route, pathname);
    const hasChildren = route.collapse?.length > 0;
    const noCollapse = Boolean(route.noCollapse);
    const alwaysOpen = noCollapse && hasChildren;
    const open = alwaysOpen ? true : Boolean(openKeys[key]);
    const navigateOnClick = !hasChildren || noCollapse;

    return (
      <React.Fragment key={key}>
        <ListItemButton
          selected={active}
          sx={itemSx(active, depth)}
          onClick={() => {
            if (navigateOnClick) {
              handleRoute(route);
            } else {
              setOpenKeys((current) => ({ ...current, [key]: !open }));
            }
          }}
        >
          {route.icon && <ListItemIcon>{route.icon}</ListItemIcon>}
          <ListItemText
            primary={route.name}
            primaryTypographyProps={{
              fontSize: depth === 0 ? 13 : 12,
              fontWeight: active ? 800 : 600,
              noWrap: true,
            }}
          />
          {hasChildren && !noCollapse &&
            (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {route.collapse.map((child) => renderRoute(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const orgName =
    normalizedDetails?.organizationName ||
    vendorProfile?.organization?.name ||
    normalizedDetails?.businessName ||
    "Fixed Ops Portal";

  const userDisplayName = (() => {
    const ud = normalizedDetails;
    const first = (ud?.firstName || user?.firstName)?.trim();
    const last = (ud?.lastName || user?.lastName)?.trim();
    const full = [first, last].filter(Boolean).join(" ");
    const realEmail = (e) => (isSyntheticEmail(e) ? null : e);
    return (
      full ||
      user?.displayName ||
      ud?.displayName ||
      user?.username ||
      ud?.username ||
      realEmail(user?.email) ||
      realEmail(ud?.email) ||
      "—"
    );
  })();

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  const userDetails = normalizedDetails;
  const currentSystemName = userDetails
    ? systemNameForAppMode(getAppMode(userDetails))
    : null;
  const pinnedRouteForSystem = currentSystemName
    ? userDetails?.systemDefaultRoutes?.[currentSystemName] || null
    : null;
  const isHomePinned = pinnedRouteForSystem === pathname;
  const showHomePinButton =
    userDetails != null &&
    userDetails.organizationId !== BACKOFFICE_ORG_ID &&
    currentSystemName != null;
  const [pinningHome, setPinningHome] = useState(false);

  const handleSetHome = async () => {
    if (pinningHome || !currentSystemName) return;
    setPinningHome(true);
    try {
      await setMySystemDefaultRoute(currentSystemName, pathname);
      await dispatch(getUser());
      const label =
        SYSTEM_DISPLAY_NAMES[getAppMode(userDetails)] || currentSystemName;
      toast.success(`Set as your ${label} home`);
    } catch (err) {
      console.error("Failed to set home route:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save your home page. Try again.";
      toast.error(message);
    } finally {
      setPinningHome(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${cc.navy} 0%, ${cc.navy2} 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.5 }}>
        <Box
          component={NavLink}
          to={homeRoute}
          onClick={onNavigate}
          sx={{
            display: "inline-block",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover img": { opacity: 0.85 },
          }}
          aria-label="Go to home"
        >
          <Box
            component="img"
            src={Logo}
            alt="Flex2"
            sx={{ width: 160, maxWidth: "100%", display: "block" }}
          />
        </Box>
        <Box
          component={onSwitchSystem ? "button" : "div"}
          type={onSwitchSystem ? "button" : undefined}
          onClick={onSwitchSystem}
          aria-label={onSwitchSystem ? "Switch to a different system" : undefined}
          sx={{
            mt: 0.75,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: FIXED_OPS_ACCENT_HOVER,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            background: "none",
            border: "none",
            p: 0,
            cursor: onSwitchSystem ? "pointer" : "default",
            "&:hover": onSwitchSystem
              ? { color: "#90caf9", textDecoration: "underline" }
              : undefined,
          }}
        >
          <BuildIcon sx={{ fontSize: 14 }} />
          Fixed Operations
        </Box>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" dense disablePadding>
          {visibleRoutes.map((route) => renderRoute(route))}
        </List>
      </Box>
      <Box
        sx={{
          mx: 1.25,
          mb: 2,
          p: 1.25,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "10px",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          Organization
        </Typography>
        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
          {formatOrgName(orgName, normalizedDetails)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          User
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>
            {userDisplayName}
          </Typography>
        </Box>
        <RolesRow userDetails={normalizedDetails} />
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Chip
            icon={<CircleIcon sx={{ color: `${FIXED_OPS_ACCENT_HOVER} !important` }} />}
            label="Operational"
            size="small"
            sx={{
              height: 22,
              bgcolor: "rgba(25,118,210,0.14)",
              color: "rgba(255,255,255,0.88)",
              fontSize: 10,
              fontWeight: 700,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {showHomePinButton ? (
              <Tooltip
                title={
                  isHomePinned
                    ? `This is your ${currentSystemName} home`
                    : `Set this page as my home for ${currentSystemName}`
                }
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSetHome}
                    disabled={pinningHome}
                    aria-label="Set as my home"
                    sx={{
                      color: isHomePinned ? FIXED_OPS_ACCENT_HOVER : "rgba(255,255,255,0.65)",
                      "&:hover": { color: isHomePinned ? "#90caf9" : "#fff" },
                    }}
                  >
                    {isHomePinned ? (
                      <HomeIcon fontSize="small" />
                    ) : (
                      <HomeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Sign out" placement="top">
              <IconButton
                size="small"
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: "#ff8e7a" },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

FixedOpsPortalSidebar.propTypes = {
  onNavigate: PropTypes.func,
  onSwitchSystem: PropTypes.func,
};

export default FixedOpsPortalSidebar;
